const INTERCOM_SCRIPT_ID = "silver-drive-intercom-script";
const ADMIN_ROUTE = /^\/admin(?:\/|$)/;
const SCRIPT_LOAD_TIMEOUT = 10000;
const MAX_LOAD_ATTEMPTS = 3;

let scriptPromise;
let bootPromise;
let booted = false;

const isAdminRoute = () => ADMIN_ROUTE.test(window.location.pathname);

const getAppId = () => (
  window.SILVER_DRIVE_INTERCOM_APP_ID || process.env.REACT_APP_INTERCOM_APP_ID
);

const getSettings = () => ({
  app_id: getAppId(),
  alignment: "right",
  horizontal_padding: 24,
  vertical_padding: 24,
  hide_default_launcher: false,
});

const isLoadedIntercom = () => (
  typeof window.Intercom === "function" && !window.Intercom.q
);

const installIntercomStub = () => {
  if (typeof window.Intercom === "function") {
    return;
  }

  const stub = (...args) => stub.q.push(args);
  stub.q = [];
  window.Intercom = stub;
};

const waitForIntercom = () => new Promise((resolve, reject) => {
  const deadline = Date.now() + SCRIPT_LOAD_TIMEOUT;

  const check = () => {
    if (isLoadedIntercom()) {
      resolve();
      return;
    }

    if (Date.now() >= deadline) {
      reject(new Error("Intercom script timed out."));
      return;
    }

    window.setTimeout(check, 50);
  };

  check();
});

const removeIntercomScript = () => {
  document.getElementById(INTERCOM_SCRIPT_ID)?.remove();
};

const loadIntercomOnce = () => {
  const appId = getAppId();
  if (!appId) {
    return Promise.reject(new Error("Intercom app ID is not configured."));
  }

  window.intercomSettings = getSettings();
  installIntercomStub();

  const script = document.getElementById(INTERCOM_SCRIPT_ID) || document.createElement("script");
  if (!script.id) {
    script.id = INTERCOM_SCRIPT_ID;
    script.async = true;
    script.src = `https://widget.intercom.io/widget/${appId}`;
    document.head.appendChild(script);
  }

  return waitForIntercom();
};

const loadIntercom = () => {
  if (isAdminRoute()) {
    return Promise.reject(new Error("Intercom is disabled on admin routes."));
  }

  if (isLoadedIntercom()) {
    return Promise.resolve();
  }

  if (scriptPromise) {
    return scriptPromise;
  }

  scriptPromise = (async () => {
    let lastError;

    for (let attempt = 1; attempt <= MAX_LOAD_ATTEMPTS; attempt += 1) {
      try {
        await loadIntercomOnce();
        return;
      } catch (error) {
        lastError = error;
        removeIntercomScript();
        if (attempt < MAX_LOAD_ATTEMPTS) {
          await new Promise((resolve) => {
            window.setTimeout(resolve, 500 * (2 ** (attempt - 1)));
          });
        }
      }
    }

    throw lastError;
  })().catch((error) => {
    scriptPromise = undefined;
    throw error;
  });

  return scriptPromise;
};

const showMessenger = () => {
  if (isAdminRoute() || !isLoadedIntercom()) {
    return false;
  }

  window.Intercom("show");
  return true;
};

export const bootIntercom = async () => {
  if (booted) {
    return;
  }

  if (bootPromise) {
    return bootPromise;
  }

  bootPromise = loadIntercom()
    .then(() => {
      if (isAdminRoute()) {
        throw new Error("Intercom is disabled on admin routes.");
      }

      window.Intercom("boot", getSettings());
      booted = true;
    })
    .catch((error) => {
      bootPromise = undefined;
      throw error;
    });

  return bootPromise;
};

export const updateIntercom = () => {
  if (!booted || !isLoadedIntercom() || isAdminRoute()) {
    return;
  }

  window.Intercom("update", { last_request_at: Math.floor(Date.now() / 1000) });
};

export const openChat = async () => {
  if (typeof window === "undefined" || isAdminRoute()) {
    return false;
  }

  try {
    await bootIntercom();
    return showMessenger();
  } catch {
    return false;
  }
};

export const shutdownIntercom = () => {
  if (typeof window === "undefined" || !booted || !isLoadedIntercom()) {
    return;
  }

  window.Intercom("hide");
  window.Intercom("shutdown");
  booted = false;
  bootPromise = undefined;
};